import { TagOption } from '@tw/tw-component-library';
import { toTagOptions } from '../../../../../utils/transformers';

export interface CreatorPersonSource {
  createdBy?: unknown;
  createdByName?: unknown;
  createdByEmail?: unknown;
  currentUser?: {
    userEmail?: string | null;
    userDisplayName?: string | null;
  } | null;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const isEmail = (value: string): boolean => EMAIL_PATTERN.test(value);

export const hasWorkflowCreator = (createdBy: unknown): boolean => {
  if (typeof createdBy === 'string') {
    return createdBy.trim() !== '';
  }

  return typeof createdBy === 'object' && createdBy !== null;
};

const isGuid = (value: string): boolean => GUID_PATTERN.test(value);

const namesMatch = (left: string, right: string): boolean =>
  left.trim().toLowerCase() === right.trim().toLowerCase();

const readRecordEmail = (record: Record<string, unknown>): string => {
  const candidates = [
    record.mail,
    record.email,
    record.userEmail,
    record.value,
  ];

  return (
    candidates
      .map(readString)
      .find((value) => isEmail(value)) ?? ''
  );
};

const readRecordLabel = (record: Record<string, unknown>): string => {
  const label = readString(
    record.label ?? record.displayName ?? record.userDisplayName
  );

  if (!label || isEmail(label) || isGuid(label)) {
    return '';
  }

  return label;
};

const emailFromCurrentUser = (
  source: CreatorPersonSource,
  identities: string[]
): string => {
  const userEmail = readString(source.currentUser?.userEmail);
  if (!isEmail(userEmail)) {
    return '';
  }

  if (identities.length === 0) {
    return userEmail;
  }

  const userName = readString(source.currentUser?.userDisplayName);
  const matchesCreator = identities.some(
    (identity) =>
      namesMatch(identity, userEmail) ||
      (userName !== '' && namesMatch(identity, userName))
  );

  return matchesCreator ? userEmail : '';
};

/**
 * Tag picker chip for the person who created the request.
 * Value is their mail address, matching a tag chosen in the person search.
 */
export const buildCreatorPersonTag = (
  source: CreatorPersonSource
): TagOption | null => {
  const createdByName = readString(source.createdByName);
  const createdByEmail = readString(source.createdByEmail);
  const createdByString = readString(source.createdBy);
  const createdByRecord = isRecord(source.createdBy) ? source.createdBy : null;
  const recordEmail = createdByRecord ? readRecordEmail(createdByRecord) : '';
  const recordLabel = createdByRecord ? readRecordLabel(createdByRecord) : '';

  const identities = [
    createdByName,
    createdByEmail,
    createdByString,
    recordEmail,
    recordLabel,
  ].filter((identity) => identity !== '');

  const email =
    (isEmail(createdByEmail) ? createdByEmail : '') ||
    (isEmail(createdByString) ? createdByString : '') ||
    recordEmail ||
    emailFromCurrentUser(source, identities);

  const label =
    (createdByName && !isEmail(createdByName) && !isGuid(createdByName)
      ? createdByName
      : '') ||
    recordLabel ||
    (createdByString && !isEmail(createdByString) && !isGuid(createdByString)
      ? createdByString
      : '') ||
    readString(source.currentUser?.userDisplayName) ||
    email;

  if (!isEmail(email)) {
    return null;
  }

  return {
    value: email,
    label: label || email,
    disabled: false,
  };
};

export const applyDefaultGivenReceivedByPerson = (
  data: Record<string, unknown>
): void => {
  if (toTagOptions(data.givenReceivedByPerson).length > 0) {
    return;
  }

  const tag = buildCreatorPersonTag({
    createdBy: data.createdBy,
    createdByName: data.createdByName,
    createdByEmail: data.createdByEmail,
  });

  if (!tag) {
    return;
  }

  data.givenReceivedByPerson = [tag];
};
